/**
 * Locale-aware share URLs.
 *
 * Detail pages build their share URL from the canonical (English, un-prefixed)
 * path — `${SITE_URL}/blogs/<slug>` — because that is also what the JSON-LD
 * `@id` and `<link rel="canonical">` use. But a visitor reading
 * `/ko/blogs/<slug>` who taps "Share on X" should share the KOREAN page, not
 * bounce their followers to the English one (the 2026-11-01 X post sent ~900
 * Korean visitors to the Korean post; every re-share pointed at English).
 *
 * `localizeSiteUrl` maps a canonical on-site URL to the same page in the
 * viewer's locale, following our `localePrefix: 'as-needed'` routing:
 *   - default locale (en) → unchanged (no prefix)
 *   - any other supported locale → `/<locale>` inserted after the origin
 *   - already locale-prefixed, off-site, or unsupported locale → unchanged
 *
 * Pure (no React / Next imports) so the share components (client) and the
 * unit tests can both use it. The share components apply it themselves, so
 * every content type that renders <SocialShare /> or <ToolsRow /> (blog,
 * tutorial, Creating a Scene, flogger, course topic) gets the same behaviour.
 */
import { defaultLocale, locales } from './config'
import { SITE_URL } from '../site-url'

function isSupportedLocale(value: string): boolean {
  return (locales as readonly string[]).includes(value)
}

export function localizeSiteUrl(
  url: string,
  locale: string,
  siteUrl: string = SITE_URL,
): string {
  if (!url || locale === defaultLocale || !isSupportedLocale(locale)) return url

  const origin = siteUrl.replace(/\/$/, '')
  if (!url.startsWith(origin)) return url

  const rest = url.slice(origin.length)
  // Guard against a look-alike host (`https://www.theduchy.com.evil.io/...`):
  // what follows the origin must be the end, a path, a query or a fragment.
  if (rest !== '' && !/^[/?#]/.test(rest)) return url

  // Already locale-prefixed (`/ko/...`, `/ko`, `/ko?x`) → leave alone.
  const firstSegment = rest.match(/^\/([^/?#]+)/)?.[1]
  if (firstSegment && isSupportedLocale(firstSegment)) return url

  return `${origin}/${locale}${rest === '/' ? '' : rest}`
}
